import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import StatsWrapper from '../utils/StatsWrapper';

/**
 * Presentational autocomplete dropdown for the search box. Hero.jsx owns the
 * suggestions data (useSearchSuggestions) and keyboard navigation (it must
 * intercept Arrow/Enter/Escape on its own <input>) - this component renders
 * the listbox, closes on an outside click (same pattern as FilterMenu.jsx),
 * and fires the shown/clicked tracking events.
 *
 * @param {string} query - current (raw) search box value, for tracking only
 * @param {string[]} suggestions
 * @param {number} activeIndex - -1 when nothing is keyboard-highlighted
 * @param {(term: string, index: number, method: 'click') => void} onSelect
 * @param {() => void} onClose
 */
function SearchSuggestions({ query, suggestions, activeIndex, onSelect, onClose }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (suggestions.length > 0) {
      StatsWrapper.track('search.suggestions_shown', { query, count: suggestions.length });
    }
  }, [suggestions, query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-suggestions-container')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (suggestions.length === 0) return null;

  const handleSelect = (term, index) => {
    onSelect(term, index, 'click');
  };

  return (
    <div
      ref={containerRef}
      className="search-suggestions-container absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg overflow-hidden"
      dir="ltr"
    >
      <ul id="search-suggestions-listbox" role="listbox" aria-label="Search suggestions">
        {suggestions.map((term, index) => (
          <li
            key={term}
            id={`search-suggestion-${index}`}
            role="option"
            aria-selected={index === activeIndex}
            className={`px-4 py-2 text-gray-800 cursor-pointer text-sm md:text-base ${
              index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
            // onMouseDown (not onClick) fires before the input's onBlur-driven
            // close logic could otherwise race it away.
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(term, index);
            }}
          >
            {term}
          </li>
        ))}
      </ul>
    </div>
  );
}

SearchSuggestions.propTypes = {
  query: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SearchSuggestions;
